import { Box, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { DashedAddButton, DashedAddButtonVariant } from "../../../../../../shared/components/buttons/DashedAddButton"
import { HoverActionMenu } from "../../../../../../shared/components/table/HoverActionMenu"

import { CategoryFormValues } from "../menuTemplatesTypes"
import { DishCard } from "./DishCard"
import { DestructiveMenuItem } from "../../../../../../shared/components/menus/DestructiveMenuItem"

const useStyles = makeStyles()((theme) => ({
    block: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".625rem",
        background: theme.palette.common.white,
        overflow: "hidden",
    },
    head: {
        padding: ".75rem 1rem",
        background: theme.palette.common.white,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    headText: {
        minWidth: 0,
    },
    name: {
        fontWeight: 700,
    },
    count: {
        color: theme.palette.tailwind.slate[500],
    },
    menuButton: {
        color: theme.palette.tailwind.slate[500],
    },
    dishes: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(12.5rem, 1fr))",
        gap: "1rem",
        padding: "1rem 2rem 1.5rem",
    },
}))

type Props = Readonly<{
    category: CategoryFormValues
    dishErrorFlags: ReadonlyArray<boolean>
    onEditName: () => void
    onRemove: () => void
    onAddDish: () => void
    onOpenDish: (dishIndex: number) => void
    onRemoveDish: (dishIndex: number) => void
}>

export function TemplateCategoryBlock({
    category,
    dishErrorFlags,
    onEditName,
    onRemove,
    onAddDish,
    onOpenDish,
    onRemoveDish,
}: Props) {
    const { classes } = useStyles()

    return (
        <Stack className={classes.block}>
            <Stack direction="row" alignItems="center" spacing="1.5rem" className={classes.head}>
                <Stack className={classes.headText} flexGrow={1}>
                    <Typography variant="subtitle1" className={classes.name}>
                        {category.name}
                    </Typography>
                    <LocalizedStrict
                        id="merchant-menu-templates-category-dish-count"
                        vars={{ count: category.dishes.length }}
                    >
                        <Typography variant="body2" className={classes.count}>
                            {category.dishes.length} dishes
                        </Typography>
                    </LocalizedStrict>
                </Stack>
                <LocalizedStrict id="merchant-menu-templates-category-edit-button">
                    <StandardButton fullWidth={false} size="small" variant="outlined" onClick={onEditName}>
                        Edit
                    </StandardButton>
                </LocalizedStrict>
                <HoverActionMenu iconButtonClassName={classes.menuButton}>
                    {(closeMenu) => (
                        <LocalizedStrict id="merchant-menu-templates-category-delete-item">
                            <DestructiveMenuItem
                                onClick={() => {
                                    closeMenu()
                                    onRemove()
                                }}
                            >
                                Delete
                            </DestructiveMenuItem>
                        </LocalizedStrict>
                    )}
                </HoverActionMenu>
            </Stack>

            <Box className={classes.dishes}>
                {category.dishes.map((dish, dishIndex) => (
                    <DishCard
                        key={dishIndex}
                        dish={dish}
                        hasError={dishErrorFlags[dishIndex] ?? false}
                        onOpen={() => onOpenDish(dishIndex)}
                        onRemove={() => onRemoveDish(dishIndex)}
                    />
                ))}
                <LocalizedStrict id="merchant-menu-templates-add-dish-card">
                    <DashedAddButton variant={DashedAddButtonVariant.CARD} onClick={onAddDish}>
                        Add dish
                    </DashedAddButton>
                </LocalizedStrict>
            </Box>
        </Stack>
    )
}
